import { AxiosError, isAxiosError } from 'axios';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import initApiV2 from '@/api/initApiV2.ts';
import { RejectedError } from '@/api/errors.ts';
import { handleApiError } from '@/api/handleApiError.ts';
import { createStreamConnection } from '@/api/assistantStream.ts';
import {
  AssistantApiErrorCode,
  AssistantConversationsApi,
  ConversationStatus,
  ModulosAssistantApi,
  type AISystemInputsRecord,
  type AssistantApiError,
  type AssistantConversationsApiAcceptAssistantActionRequest,
  type AssistantConversationsApiDismissAssistantActionRequest,
  type AssistantConversation,
  type AssistantConversationMessage,
  type AssistantConversationSummary,
  type AssistantConversationsApiDeleteAssistantConversationRequest,
  type AssistantConversationsApiGetAssistantConversationRequest,
  type AssistantConversationsApiGetAssistantConversationTurnOptionsRequest,
  type AssistantConversationsApiGetAssistantTurnOptionsRequest,
  type AssistantConversationsApiRenameAssistantConversationRequest,
  type AssistantStreamEvent,
  type CreateAISystemsAction,
  type ConversationPhase,
  type ConversationScope,
  type ConversationScopeView,
  type ModulosAssistantApiCreateTempEvidenceForConversationRequest,
  type ReviseActionReply,
  ScopeType,
  type TurnAccepted,
  type TurnInputs,
  type TurnOutcome,
} from '@/clients/v2';
import { useOrganizationsStore } from '@/stores/v2/OrganizationStore.ts';
import type { ChatMessage, ChatThought } from '@/features/assistant/types';

const CONVERSATIONS_PAGE_SIZE = 20;
const RESEND_DELAY_MS = 1000;

type SendTurn = (clientMessageId: string) => Promise<{ data: TurnAccepted }>;

interface Turn {
  clientMessageId: string;
  send: SendTurn;
  onAccepted: (accepted: TurnAccepted) => void;
}

function isAssistantApiError(detail: unknown): detail is AssistantApiError {
  return typeof detail === 'object' && detail !== null && 'code' in detail && 'detail' in detail;
}

function assistantApiError(error: unknown): AssistantApiError | null {
  if (!isAxiosError(error)) return null;
  const detail: unknown = error.response?.data?.detail;
  return isAssistantApiError(detail) ? detail : null;
}

// A 4xx: the server refused the request and stored nothing.
function isRejection(error: unknown): error is AxiosError {
  const status = isAxiosError(error) ? error.response?.status : undefined;
  return status !== undefined && status >= 400 && status < 500;
}

function throwApiError(error: unknown): never {
  if (isRejection(error)) {
    const rejection = assistantApiError(error);
    if (rejection) {
      throw new RejectedError(
        rejection.detail,
        rejection.code,
        rejection.unavailable_inputs ?? null,
      );
    }
    try {
      handleApiError(error);
    } catch (classified) {
      throw new RejectedError(
        classified instanceof Error ? classified.message : String(classified),
      );
    }
  }
  handleApiError(error as AxiosError);
  throw error;
}

// A send without a response may have arrived; the same client_message_id is idempotent, so resend once.
async function withOneResend<T>(send: () => Promise<T>): Promise<T> {
  try {
    return await send();
  } catch (error) {
    if (!isAxiosError(error) || error.response) throw error;
    await new Promise((resolve) => setTimeout(resolve, RESEND_DELAY_MS));
    return send();
  }
}

function toChatMessage(message: AssistantConversationMessage): ChatMessage {
  const timestamp = new Date(message.created_at);
  if (message.role === 'user') {
    return {
      id: message.id,
      role: 'user',
      content: message.content,
      timestamp,
      outcome: message.outcome,
      inputs: message.inputs.type === ScopeType.AiSystem ? message.inputs : undefined,
    };
  }
  // Keeps what the answer offered (the Scout actions it proposed, tables to download), so a reload renders the same.
  return {
    id: message.id,
    role: 'assistant',
    content: message.content,
    message_data: message.message_data,
    actions: message.actions,
    timestamp,
  };
}

export const useChatStore = defineStore('chat', () => {
  const organizationsStore = useOrganizationsStore();

  const isInvestigating = ref(false);
  const messages = ref<ChatMessage[]>([]);
  const thoughts = ref<ChatThought[]>([]);
  const connectionLost = ref(false);
  // Settled turns, from the stream or caught up by reconcile; a refused send adds none.
  const settledTurns = ref(0);
  const conversation = ref<AssistantConversation | null>(null);
  const conversationId = computed(() => conversation.value?.id ?? null);
  // What a first question was sent about, until the server names its conversation.
  const startingScope = ref<{ view: ConversationScopeView; phase: ConversationPhase } | null>(null);
  const conversations = ref<AssistantConversationSummary[]>([]);
  const loadingConversations = ref(false);
  const hasMoreConversations = ref(true);
  const nextConversationsPage = ref(1);
  const conversationsFilter = ref<{ scopeType: ScopeType; aiSystemId: string | null } | null>(null);
  const conversationsSearch = ref<string | null>(null);
  const requestedSearch = ref<string | null>(null);
  const conversationsTotal = ref<number | null>(null);
  // By AI System id or 'global'; the history filter offers them.
  const seenScopes = ref(
    new Map<string, { view: ConversationScopeView; phase: ConversationPhase }>(),
  );
  const assistantDisabled = ref(false);
  const scoutAvailable = computed(() => organizationsStore.chatEnabled && !assistantDisabled.value);

  function raise(error: unknown): never {
    if (assistantApiError(error)?.code === AssistantApiErrorCode.AssistantDisabled) {
      assistantDisabled.value = true;
    }
    throwApiError(error);
  }

  // Bumped on every session change: a turn or load started before it is dropped when it lands.
  let generation = 0;
  // A question whose send got no definite answer; a retry resends it under the same id.
  let unconfirmedTurn: Turn | null = null;
  // The session a rerun request is in flight for: until the server takes it, it still holds the old
  // failure, and a catch-up would restore it. A catch-up held off meanwhile runs once it is taken.
  let rerunAt: number | null = null;
  let reruns = 0;
  let catchUpHeld = false;

  const stream = createStreamConnection({
    onEvent: handleEvent,
    onConnectionLost: () => {
      connectionLost.value = true;
      isInvestigating.value = false;
    },
    onInvestigatingChange: (value) => {
      isInvestigating.value = value;
    },
  });

  const lastQuestion = () => {
    for (let i = messages.value.length - 1; i >= 0; i -= 1) {
      if (messages.value[i].role === 'user') return messages.value[i];
    }
    return null;
  };

  // A send without a definite answer blocks the next one: a second question could land behind it.
  const unconfirmedQuestionId = computed(() => {
    const question = lastQuestion();
    return question?.delivery === 'unconfirmed' ? question.id : null;
  });

  const retryableMessageId = computed(() => {
    const question = lastQuestion();
    if (!question || isInvestigating.value) return null;
    return question.delivery === 'unconfirmed' || question.outcome?.state === 'failed'
      ? question.id
      : null;
  });

  function patchMessage(id: string, patch: Partial<ChatMessage>) {
    messages.value = messages.value.map((message) =>
      message.id === id ? { ...message, ...patch } : message,
    );
  }

  // An outcome for an unconfirmed question proves the server stored it.
  function setOutcome(questionId: string, outcome: TurnOutcome) {
    if (unconfirmedTurn?.clientMessageId === questionId) {
      unconfirmedTurn = null;
      patchMessage(questionId, { outcome, delivery: undefined });
      return;
    }
    patchMessage(questionId, { outcome });
  }

  // The last message the server has; a stream opened from it replays what came after.
  function lastConfirmedMessageId(): string | undefined {
    for (let i = messages.value.length - 1; i >= 0; i -= 1) {
      if (!messages.value[i].delivery) return messages.value[i].id;
    }
    return undefined;
  }

  // Catches up an outcome missed during a reconnect.
  async function reconcileMessages() {
    const currentId = conversationId.value;
    const startedAt = generation;
    const rerunsAtStart = reruns;
    if (!currentId) return;
    try {
      const api = await initApiV2(AssistantConversationsApi);
      if (!api) return;
      const { data } = await api.listAssistantConversationMessages({ conversationId: currentId });
      if (startedAt !== generation || reruns !== rerunsAtStart || rerunAt === generation) return;
      // A question whose send is still in flight is not on the server yet.
      const serverIds = new Set(data.map((message) => message.id));
      const inFlight = messages.value.filter(
        (message) => message.role === 'user' && !serverIds.has(message.id),
      );
      const pendingId = lastQuestion()?.outcome?.state === 'pending' ? lastQuestion()?.id : null;
      messages.value = [...data.map(toChatMessage), ...inFlight];
      // An outcome caught up here counts like one the stream delivered.
      const caughtUp = messages.value.find((message) => message.id === pendingId);
      if (caughtUp?.outcome && caughtUp.outcome.state !== 'pending') settledTurns.value += 1;
    } catch {
      // The next event or load refreshes the thread; nothing to report here.
    }
  }

  function applyTitle(title: string) {
    const current = conversation.value;
    if (!current || current.title === title) return;
    conversation.value = { ...current, title };
    conversations.value = conversations.value.map((item) =>
      item.id === current.id ? { ...item, title } : item,
    );
  }

  function handleEvent(event: AssistantStreamEvent) {
    switch (event.type) {
      case 'thought':
        thoughts.value.push({ content: event.content, timestamp: new Date().toISOString() });
        break;
      case 'status':
        isInvestigating.value = event.status === ConversationStatus.Thinking;
        if (
          event.status === ConversationStatus.Idle &&
          lastQuestion()?.outcome?.state === 'pending' &&
          !unconfirmedTurn
        ) {
          if (rerunAt === generation) catchUpHeld = true;
          else reconcileMessages();
        }
        break;
      case 'final':
        isInvestigating.value = false;
        // A reconnect replays answers after the last message: one already shown is skipped.
        if (!messages.value.some((message) => message.id === event.id)) {
          messages.value.push({
            id: event.id,
            role: 'assistant',
            content: event.content,
            // Carries what Scout offers with the reply (the Scout actions it proposes, tables to download).
            message_data: event.message_data,
            actions: event.actions,
            timestamp: new Date(),
          });
        }
        setOutcome(event.answers_message_id, { state: 'answered' });
        settledTurns.value += 1;
        break;
      case 'error':
        isInvestigating.value = false;
        if (event.message_id) setOutcome(event.message_id, { state: 'failed', code: event.code });
        settledTurns.value += 1;
        break;
      case 'title':
        applyTitle(event.title);
        break;
    }
  }

  // Set synchronously, so a second submit in the same tick finds the chat busy.
  function beginTurn(query: string, inputs: AISystemInputsRecord | undefined): string {
    const clientMessageId = crypto.randomUUID();
    thoughts.value = [];
    connectionLost.value = false;
    stream.resetReconnectAttempts();
    messages.value.push({
      id: clientMessageId,
      role: 'user',
      content: query,
      timestamp: new Date(),
      outcome: { state: 'pending' },
      delivery: 'sending',
      inputs,
    });
    isInvestigating.value = true;
    return clientMessageId;
  }

  function dropQuestion(clientMessageId: string) {
    messages.value = messages.value.filter((message) => message.id !== clientMessageId);
    isInvestigating.value = false;
    if (!conversation.value) startingScope.value = null;
  }

  // The 202 can land after the stream already delivered the outcome; a settled question stays settled.
  function isPending(questionId: string) {
    return (
      messages.value.find((message) => message.id === questionId)?.outcome?.state === 'pending'
    );
  }

  function busyAfterAccept(questionId: string, accepted: AssistantConversation) {
    return accepted.status === ConversationStatus.Thinking && isPending(questionId);
  }

  // A rejection removes the question and throws so the caller restores its composer. Other failures leave it
  // unconfirmed for a retry. A session the user left is untouched, but the rejection is still thrown so the text
  // goes back to that chat's draft.
  async function deliver(turn: Turn, startedAt: number) {
    try {
      const { data } = await withOneResend(() => turn.send(turn.clientMessageId));
      if (startedAt !== generation) return;
      unconfirmedTurn = null;
      turn.onAccepted(data);
      patchMessage(turn.clientMessageId, { delivery: undefined });
      isInvestigating.value = busyAfterAccept(turn.clientMessageId, data.conversation);
    } catch (error) {
      if (startedAt !== generation) {
        if (isRejection(error)) raise(error);
        return;
      }
      if (isRejection(error)) {
        unconfirmedTurn = null;
        dropQuestion(turn.clientMessageId);
      } else {
        unconfirmedTurn = turn;
        isInvestigating.value = false;
        patchMessage(turn.clientMessageId, { delivery: 'unconfirmed' });
      }
      raise(error);
    }
  }

  function connectStream(forConversationId: string, lastMessageId?: string) {
    stream.connect(forConversationId, lastMessageId);
  }

  /**
   * `startingScope`: the scope as shown, held until the server names the conversation.
   * `shownInputs`: shown on the question until its recorded inputs load.
   */
  const startConversation = async ({
    startingScope: shownScope,
    shownInputs,
    ...params
  }: {
    scope: ConversationScope;
    startingScope: { view: ConversationScopeView; phase: ConversationPhase };
    query: string;
    inputs: TurnInputs;
    shownInputs?: AISystemInputsRecord;
  }) => {
    const startedAt = generation;
    startingScope.value = shownScope;
    const clientMessageId = beginTurn(params.query, shownInputs);
    const api = await initApiV2(AssistantConversationsApi);
    if (!api) {
      dropQuestion(clientMessageId);
      return;
    }

    await deliver(
      {
        clientMessageId,
        send: (id) =>
          api.startAssistantConversation({
            startConversationRequest: { client_message_id: id, ...params },
          }),
        onAccepted: (accepted) => {
          generation += 1;
          // Named while the starting scope is still set, so the draft watcher sees the same chat.
          conversation.value = accepted.conversation;
          startingScope.value = null;
          // Answers given before the stream opened are replayed after the question.
          connectStream(accepted.conversation.id, accepted.message_id);
        },
      },
      startedAt,
    );
  };

  const sendMessage = async ({
    shownInputs,
    ...params
  }: {
    query: string;
    inputs: TurnInputs;
    shownInputs?: AISystemInputsRecord;
    reply_to?: ReviseActionReply;
  }) => {
    const currentId = conversationId.value;
    if (!currentId) return;

    const startedAt = generation;
    const clientMessageId = beginTurn(params.query, shownInputs);
    const api = await initApiV2(AssistantConversationsApi);
    if (!api) {
      dropQuestion(clientMessageId);
      return;
    }

    await deliver(
      {
        clientMessageId,
        send: (id) =>
          api.sendAssistantMessage({
            conversationId: currentId,
            sendMessageRequest: { client_message_id: id, ...params },
          }),
        onAccepted: (accepted) => {
          conversation.value = accepted.conversation;
          // A turn the stream already settled needs no stream: the panel may be closed.
          if (!stream.isStreamConnected.value && isPending(clientMessageId)) {
            connectStream(currentId, lastConfirmedMessageId());
          }
        },
      },
      startedAt,
    );
  };

  const retryTurn = async () => {
    const startedAt = generation;

    if (unconfirmedTurn) {
      const turn = unconfirmedTurn;
      thoughts.value = [];
      connectionLost.value = false;
      patchMessage(turn.clientMessageId, { delivery: 'sending' });
      isInvestigating.value = true;
      await deliver(turn, startedAt);
      return;
    }

    const currentId = conversationId.value;
    const question = lastQuestion();
    if (!currentId || question?.outcome?.state !== 'failed') return;
    const api = await initApiV2(AssistantConversationsApi);
    if (!api) return;

    thoughts.value = [];
    connectionLost.value = false;
    stream.resetReconnectAttempts();
    isInvestigating.value = true;
    const failure = question.outcome;
    setOutcome(question.id, { state: 'pending' });

    reruns += 1;
    rerunAt = startedAt;
    catchUpHeld = false;
    try {
      const { data } = await api.retryAssistantTurn({ conversationId: currentId });
      if (startedAt !== generation) return;
      conversation.value = data.conversation;
      isInvestigating.value = busyAfterAccept(question.id, data.conversation);
      if (!stream.isStreamConnected.value && isPending(question.id)) {
        connectStream(currentId, data.message_id);
      }
    } catch (err) {
      if (startedAt !== generation) return;
      isInvestigating.value = false;
      setOutcome(question.id, failure);
      raise(err);
    } finally {
      if (rerunAt === startedAt) rerunAt = null;
    }
    if (startedAt === generation && catchUpHeld && isPending(question.id)) reconcileMessages();
  };

  // Ordered by last activity, so a conversation can move between pages: later pages skip known ids.
  // The latest request wins; a reset in flight holds off a load-more.
  let listRequest = 0;
  let resetInFlight = false;
  const loadConversations = async (reset = false) => {
    const search = reset ? requestedSearch.value : conversationsSearch.value;
    if (!reset && (resetInFlight || !hasMoreConversations.value)) return;

    listRequest += 1;
    const request = listRequest;
    if (reset) resetInFlight = true;
    loadingConversations.value = true;
    try {
      const api = await initApiV2(AssistantConversationsApi);
      if (!api) return;

      const { data } = await api.listAssistantConversations({
        size: CONVERSATIONS_PAGE_SIZE,
        page: reset ? 1 : nextConversationsPage.value,
        scopeType: conversationsFilter.value?.scopeType ?? null,
        aiSystemId: conversationsFilter.value?.aiSystemId ?? null,
        title: search,
      });
      if (request !== listRequest) return;

      conversationsSearch.value = search;
      const known = reset ? [] : conversations.value;
      const knownIds = new Set(known.map((item) => item.id));
      conversations.value = [...known, ...data.items.filter((item) => !knownIds.has(item.id))];
      // Newest first: the first page carries a scope's current name and phase.
      const setFromPage = new Set<string>();
      for (const item of data.items) {
        const key =
          item.scope.type === ScopeType.AiSystem ? item.scope.ai_system.id : ScopeType.Global;
        if (setFromPage.has(key) || (!reset && seenScopes.value.has(key))) continue;
        setFromPage.add(key);
        seenScopes.value.set(key, { view: item.scope, phase: item.phase });
      }
      conversationsTotal.value = data.total;
      nextConversationsPage.value = data.page + 1;
      hasMoreConversations.value = data.page < data.pages;
    } catch (err) {
      raise(err);
    } finally {
      if (request === listRequest) {
        loadingConversations.value = false;
        resetInFlight = false;
      }
    }
  };

  const filterConversations = async (
    filter: { scopeType: ScopeType; aiSystemId: string | null } | null,
  ) => {
    conversationsFilter.value = filter;
    await loadConversations(true);
  };

  const searchConversations = async (title: string | null) => {
    requestedSearch.value = title;
    await loadConversations(true);
  };

  // A later open or a reset supersedes a load still in flight.
  let loadRequest = 0;
  const loadConversation = async (convId: string) => {
    loadRequest += 1;
    const request = loadRequest;
    try {
      const api = await initApiV2(AssistantConversationsApi);
      if (!api) return;

      const [conversationResponse, messagesResponse] = await Promise.all([
        api.getAssistantConversation({ conversationId: convId }),
        api.listAssistantConversationMessages({ conversationId: convId }),
      ]);

      if (request !== loadRequest) return;

      generation += 1;
      unconfirmedTurn = null;
      // Cleared first: a set starting scope tells watchers the conversation came from its first send.
      startingScope.value = null;
      conversation.value = conversationResponse.data;
      thoughts.value = [];
      connectionLost.value = false;
      isInvestigating.value = conversation.value.status === ConversationStatus.Thinking;
      messages.value = messagesResponse.data.map(toChatMessage);

      connectStream(conversationResponse.data.id, lastConfirmedMessageId());

      return conversation.value;
    } catch (err) {
      raise(err);
    }
  };

  const getTurnOptions = async (
    params: AssistantConversationsApiGetAssistantTurnOptionsRequest,
  ) => {
    try {
      const api = await initApiV2(AssistantConversationsApi);
      if (!api) return;

      const { data } = await api.getAssistantTurnOptions(params);
      return data;
    } catch (err) {
      raise(err);
    }
  };

  const getConversationTurnOptions = async (
    params: AssistantConversationsApiGetAssistantConversationTurnOptionsRequest,
  ) => {
    try {
      const api = await initApiV2(AssistantConversationsApi);
      if (!api) return;

      const { data } = await api.getAssistantConversationTurnOptions(params);
      return data;
    } catch (err) {
      raise(err);
    }
  };

  const getConversation = async (
    params: AssistantConversationsApiGetAssistantConversationRequest,
  ) => {
    try {
      const api = await initApiV2(AssistantConversationsApi);
      if (!api) return;

      const { data } = await api.getAssistantConversation(params);
      return data;
    } catch (err) {
      raise(err);
    }
  };

  const answerAction = async (
    send: (api: AssistantConversationsApi) => Promise<{ data: CreateAISystemsAction }>,
  ): Promise<CreateAISystemsAction | undefined> => {
    try {
      const api = await initApiV2(AssistantConversationsApi);
      if (!api) return;

      const { data } = await send(api);
      return data;
    } catch (err) {
      const rejection = assistantApiError(err);
      // Answered before: the refusal still says where the action stands.
      if (rejection?.action && rejection.code === AssistantApiErrorCode.ActionAlreadyDecided) {
        return rejection.action;
      }
      raise(err);
    }
  };

  /** Runs what Scout proposed with the chosen items. Accepting an accepted action answers its stored result. */
  const acceptAction = (params: AssistantConversationsApiAcceptAssistantActionRequest) =>
    answerAction((api) => api.acceptAssistantAction(params));

  const dismissAction = (params: AssistantConversationsApiDismissAssistantActionRequest) =>
    answerAction((api) => api.dismissAssistantAction(params));

  const refreshConversation = async () => {
    const currentId = conversationId.value;
    if (!currentId) return;
    const startedAt = generation;
    try {
      const api = await initApiV2(AssistantConversationsApi);
      if (!api) return;

      const { data } = await api.getAssistantConversation({ conversationId: currentId });
      if (startedAt !== generation) return;
      conversation.value = data;
    } catch (err) {
      raise(err);
    }
  };

  const createTempEvidenceForConversation = async (
    params: ModulosAssistantApiCreateTempEvidenceForConversationRequest,
  ) => {
    try {
      const modulosAssistantApi = await initApiV2(ModulosAssistantApi);
      if (!modulosAssistantApi) return;

      const response = await modulosAssistantApi.createTempEvidenceForConversation(params);

      if (!response) return;

      return response.data;
    } catch (err) {
      handleApiError(err as AxiosError);
    }
  };

  const renameConversation = async (
    params: AssistantConversationsApiRenameAssistantConversationRequest,
  ) => {
    try {
      const api = await initApiV2(AssistantConversationsApi);
      if (!api) return;

      const { data } = await api.renameAssistantConversation(params);

      if (conversation.value?.id === data.id) conversation.value = data;

      return data;
    } catch (err) {
      raise(err);
    }
  };

  const deleteConversation = async (
    params: AssistantConversationsApiDeleteAssistantConversationRequest,
  ) => {
    try {
      const api = await initApiV2(AssistantConversationsApi);
      if (!api) return;

      await api.deleteAssistantConversation(params);
    } catch (err) {
      if (assistantApiError(err)?.code === AssistantApiErrorCode.ConversationNotFound) return;
      raise(err);
    }
  };

  const pauseStream = () => {
    stream.disconnect();
  };

  const resumeStream = () => {
    if (!conversationId.value || stream.isStreamConnected.value) return;

    connectionLost.value = false;
    connectStream(conversationId.value, lastConfirmedMessageId());
  };

  const reset = () => {
    generation += 1;
    loadRequest += 1;
    unconfirmedTurn = null;
    stream.disconnect();

    isInvestigating.value = false;
    messages.value = [];
    thoughts.value = [];
    connectionLost.value = false;
    startingScope.value = null;
    conversation.value = null;
  };

  return {
    assistantDisabled,
    connectionLost,
    conversation,
    conversationId,
    conversations,
    conversationsFilter,
    conversationsSearch,
    conversationsTotal,
    hasMoreConversations,
    isInvestigating,
    loadingConversations,
    messages,
    retryableMessageId,
    scoutAvailable,
    seenScopes,
    settledTurns,
    startingScope,
    thoughts,
    unconfirmedQuestionId,

    acceptAction,
    createTempEvidenceForConversation,
    deleteConversation,
    dismissAction,
    filterConversations,
    getConversation,
    getConversationTurnOptions,
    getTurnOptions,
    loadConversation,
    loadConversations,
    pauseStream,
    refreshConversation,
    renameConversation,
    reset,
    resumeStream,
    retryTurn,
    searchConversations,
    sendMessage,
    startConversation,
  };
});
